import React, { useState } from 'react';
import { ChevronRight, Clock, Calendar, Check, X, AlertTriangle, Ban, Loader2, MapPin, Lock } from 'lucide-react';
import { ServizioFiduciarioService } from '../../services/apiService';
import { useToast } from '../ToastContext';
import { safeAction } from '../../utils/safeAction';
import { turnoPausaStato } from '../../utils/turnoStato';

interface Props {
    t: any;
    viewMode: 'OGGI' | 'FUTURI' | 'STORICO' | 'DA_ACCETTARE';
    onClick: () => void;
    onRefresh?: () => void; // ricarica i turni dopo accetta/rifiuta
}

export const TurnoCard: React.FC<Props> = ({ t, viewMode, onClick, onRefresh }) => {
    const { addToast } = useToast();
    const [acting, setActing] = useState<'accetta' | 'rifiuta' | null>(null);
    const [showRifiutaModal, setShowRifiutaModal] = useState(false);
    const [motivoRifiuto, setMotivoRifiuto] = useState('');

    const isAttivo = !!t.effettivoInizio && !t.effettivoFine;
    const isCompletato = !!t.effettivoInizio && !!t.effettivoFine;
    // Concluso solo dopo l'invio del report. Prima è "In Pausa" nel giorno del
    // turno e "Report" nei giorni successivi (vedi utils/turnoStato).
    const isConcluso = isCompletato && !!t.reportTrasmesso;
    const pausaStato = turnoPausaStato(t);
    // Stati di accettazione e revoca
    const isRevocato = !!t.dataRevoca;
    const stato = t.statoAccettazione || 'ASSEGNATO';
    // Turno passato e mai avviato: Accetta/Rifiuta non servono più e si nascondono.
    const isPassatoNonAvviato = (() => {
        if (!t.dataTurno || isAttivo || isCompletato) return false;
        // Confronto tra stringhe YYYY-MM-DD, solo data, così un turno di oggi
        // resta accettabile. Data locale: in UTC di notte sarebbe ancora "ieri".
        const _n = new Date();
        const oggi = `${_n.getFullYear()}-${String(_n.getMonth() + 1).padStart(2, '0')}-${String(_n.getDate()).padStart(2, '0')}`;
        return String(t.dataTurno).slice(0, 10) < oggi;
    })();
    const isDaAccettare = stato === 'ASSEGNATO' && !isAttivo && !isCompletato && !isRevocato && !isPassatoNonAvviato;
    const isRifiutato = stato === 'RIFIUTATO';
    const isAccettato = stato === 'ACCETTATO';

    // Priorità badge: revocato > rifiutato > scaduto > in servizio > pausa/report > completato > programmato
    const pausaBadge = pausaStato === 'REPORT'
        ? { badgeClass: 'bg-orange-100 text-orange-700', badgeLabel: 'Report' }
        : { badgeClass: 'bg-amber-100 text-amber-700', badgeLabel: 'In Pausa' };
    const { badgeClass, badgeLabel } = (() => {
        if (isRevocato) return { badgeClass: 'bg-rose-100 text-rose-700', badgeLabel: 'Revocato' };
        if (isRifiutato) return { badgeClass: 'bg-rose-50 text-rose-600 line-through', badgeLabel: 'Rifiutato' };
        // Turno passato e mai avviato: "Non avviato".
        if (isPassatoNonAvviato) return { badgeClass: 'bg-slate-200 text-slate-700', badgeLabel: 'Non avviato' };
        // In storico "Completato" significa turno svolto, a prescindere dal report;
        // In Pausa/Report si distinguono solo nelle viste attive.
        if (viewMode === 'STORICO') {
            return isCompletato
                ? { badgeClass: 'bg-emerald-100 text-emerald-700', badgeLabel: 'Completato' }
                : { badgeClass: 'bg-rose-100 text-rose-700', badgeLabel: 'Non Effettuato' };
        }
        if (isAttivo) return { badgeClass: 'bg-emerald-100 text-emerald-700 animate-pulse', badgeLabel: 'In Servizio' };
        if (pausaStato) return pausaBadge;
        if (isConcluso) return { badgeClass: 'bg-emerald-100 text-emerald-700', badgeLabel: 'Completato' };
        if (isDaAccettare) return { badgeClass: 'bg-amber-100 text-amber-700', badgeLabel: 'Da Accettare' };
        if (isAccettato && viewMode === 'FUTURI') return { badgeClass: 'bg-teal-100 text-teal-700', badgeLabel: 'Accettato' };
        if (viewMode === 'FUTURI') return { badgeClass: 'bg-teal-100 text-teal-700', badgeLabel: 'Programmato' };
        return { badgeClass: 'bg-emerald-100 text-emerald-700', badgeLabel: 'Da Effettuare' };
    })();

    const cardClass = isRevocato
        ? 'opacity-60 bg-rose-50/30 border-rose-200'
        : isRifiutato
            ? 'opacity-50 bg-slate-50 border-slate-200'
            : viewMode === 'STORICO'
                ? 'opacity-80 bg-slate-50 hover:opacity-100 border-slate-200'
                : viewMode === 'FUTURI'
                    ? isDaAccettare ? 'border-amber-300 hover:border-amber-500 ring-2 ring-amber-100' : 'border-teal-200 hover:border-teal-400'
                    : isAttivo
                        ? 'border-emerald-500 shadow-emerald-100/50 shadow-lg'
                        : 'border-slate-200';

    const handleAccetta = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setActing('accetta');
        try {
            await safeAction(
                { targetAction: 'TURNO_ACCETTATO', entityType: 'turno', entityId: t.id },
                () => ServizioFiduciarioService.accettaTurno(t.id),
            );
            addToast('Turno accettato', 'success');
            onRefresh?.();
        } catch {
            addToast('Errore accettazione turno', 'error');
        } finally {
            setActing(null);
        }
    };

    const handleOpenRifiuta = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowRifiutaModal(true);
    };

    const handleConfirmRifiuta = async () => {
        if (!motivoRifiuto.trim()) {
            addToast('Inserisci un motivo per il rifiuto', 'error');
            return;
        }
        setActing('rifiuta');
        try {
            await safeAction(
                { targetAction: 'TURNO_RIFIUTATO', entityType: 'turno', entityId: t.id, payload: { motivo: motivoRifiuto.trim() } },
                () => ServizioFiduciarioService.rifiutaTurno(t.id, motivoRifiuto.trim()),
            );
            addToast('Turno rifiutato', 'success');
            setShowRifiutaModal(false);
            setMotivoRifiuto('');
            onRefresh?.();
        } catch {
            addToast('Errore rifiuto turno', 'error');
        } finally {
            setActing(null);
        }
    };

    // Finché non è accettato il turno non si apre: il click mostra un toast.
    const handleCardClick = () => {
        if (isDaAccettare) {
            addToast('Accetta il turno prima di aprire la dashboard.', 'info');
            return;
        }
        onClick();
    };

    return (
        <>
            <div
                onClick={handleCardClick}
                className={`group p-5 rounded-2xl bg-white border transition-all ${isDaAccettare ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-md active:scale-[0.98]'} ${cardClass}`}
            >
                <div className="flex justify-between items-start mb-3">
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider ${badgeClass}`}>
                        {badgeLabel}
                    </span>
                    {isDaAccettare
                        ? <Lock className="text-amber-400" size={18} aria-label="Bloccato fino all'accettazione" />
                        : <ChevronRight className="text-slate-300 group-hover:text-slate-500 transition-colors" size={20} />}
                </div>
                <h3 className="font-semibold text-lg text-slate-900 truncate pr-4">{t.servizio?.nome}</h3>
                {t.servizio?.indirizzo && (
                    <div className="flex items-start gap-1.5 mt-1 text-xs text-slate-500 font-medium">
                        <MapPin size={13} className="shrink-0 mt-0.5" />
                        <span className="truncate">{t.servizio.indirizzo}</span>
                    </div>
                )}
                <div className="flex items-center gap-4 mt-2 text-xs font-medium">
                    <div className="flex items-center gap-1.5 text-slate-500">
                        <Calendar size={14} />
                        {new Date(t.dataTurno).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1.5 text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                        <Clock size={14} />
                        {t.oraInizio} - {t.oraFine}
                    </div>
                </div>

                {/* Scadenza accettazione e bottoni accetta/rifiuta */}
                {isDaAccettare && (
                    <>
                        {t.dataScadenzaAccettazione && (
                            <div className="mt-3 flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 px-2 py-1 rounded">
                                <AlertTriangle size={12}/>
                                Accetta entro {new Date(t.dataScadenzaAccettazione).toLocaleString('it-IT')}
                            </div>
                        )}
                        <div className="mt-3 flex gap-2">
                            <button onClick={handleAccetta} disabled={!!acting}
                                    className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-bold disabled:bg-slate-300 transition-colors">
                                {acting === 'accetta' ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}
                                Accetta
                            </button>
                            <button onClick={handleOpenRifiuta} disabled={!!acting}
                                    className="flex-1 flex items-center justify-center gap-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 px-3 py-2 rounded-lg text-sm font-bold disabled:opacity-60 transition-colors">
                                <X size={14}/>
                                Rifiuta
                            </button>
                        </div>
                    </>
                )}

                {/* Esito dell'azione */}
                {isRifiutato && t.motivoRifiuto && (
                    <div className="mt-3 text-[11px] text-rose-700 bg-rose-50 border border-rose-100 px-2 py-1.5 rounded">
                        <span className="font-bold">Rifiutato:</span> {t.motivoRifiuto}
                    </div>
                )}

                {/* Revoca del titolare */}
                {isRevocato && (
                    <div className="mt-3 flex items-center gap-1.5 text-[11px] text-rose-700 bg-rose-50 border border-rose-100 px-2 py-1.5 rounded">
                        <Ban size={12}/>
                        Revocato dal titolare {t.dataRevoca && `il ${new Date(t.dataRevoca).toLocaleString('it-IT')}`}
                    </div>
                )}
            </div>

            {/* Modale rifiuto */}
            {showRifiutaModal && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowRifiutaModal(false)}>
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-bold text-slate-900 mb-2">Rifiuta turno</h3>
                        <p className="text-sm text-slate-600 mb-4">
                            Stai per rifiutare il turno del <strong>{new Date(t.dataTurno).toLocaleDateString('it-IT')}</strong> ({t.oraInizio}-{t.oraFine}).
                            Il titolare riceverà notifica con il motivo.
                        </p>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Motivo *</label>
                        <textarea
                            value={motivoRifiuto}
                            onChange={e => setMotivoRifiuto(e.target.value)}
                            placeholder="Es. Indisponibilità imprevista, sono malato..."
                            rows={3}
                            className="w-full p-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 outline-none"
                            autoFocus
                        />
                        <div className="mt-4 flex gap-2 justify-end">
                            <button onClick={() => setShowRifiutaModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium">
                                Annulla
                            </button>
                            <button onClick={handleConfirmRifiuta} disabled={acting === 'rifiuta' || !motivoRifiuto.trim()}
                                    className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-bold flex items-center gap-1.5 disabled:bg-slate-300">
                                {acting === 'rifiuta' ? <Loader2 size={14} className="animate-spin"/> : <X size={14}/>}
                                Conferma Rifiuto
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
